import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// Cross-instance coordinator for attachment-passage elements during a
// live lesson. Each SlideElementAttachmentPassage rendered in view mode
// registers itself here so the clicker can walk every passage on the
// current slide (open → close, in slide order) before advancing.
//
// Component-owned local state would not work for slides with multiple
// passages: the clicker needs to ask "is any open?" and "have all been
// opened?" across instances, which only a shared store can answer.

interface PassageEntry {
  id: string;
  isOpen: boolean;
  hasBeenOpened: boolean;
}

interface LivePassageContextValue {
  passages: ReadonlyArray<PassageEntry>;
  register: (id: string) => void;
  unregister: (id: string) => void;
  setOpen: (id: string, open: boolean) => void;
  openNext: () => void;
  closeCurrent: () => void;
}

const LivePassageContext = createContext<LivePassageContextValue | null>(null);

export function LivePassageProvider({ children }: { children: ReactNode }) {
  const [passages, setPassages] = useState<PassageEntry[]>([]);

  const register = useCallback((id: string) => {
    setPassages(prev =>
      prev.some(p => p.id === id)
        ? prev
        : [...prev, { id, isOpen: false, hasBeenOpened: false }],
    );
  }, []);

  const unregister = useCallback((id: string) => {
    setPassages(prev => prev.filter(p => p.id !== id));
  }, []);

  const setOpen = useCallback((id: string, open: boolean) => {
    setPassages(prev =>
      prev.map(p =>
        p.id === id ? { ...p, isOpen: open, hasBeenOpened: p.hasBeenOpened || open } : p,
      ),
    );
  }, []);

  const openNext = useCallback(() => {
    setPassages(prev => {
      const idx = prev.findIndex(p => !p.hasBeenOpened);
      if (idx === -1) return prev;
      return prev.map((p, i) =>
        i === idx ? { ...p, isOpen: true, hasBeenOpened: true } : p,
      );
    });
  }, []);

  const closeCurrent = useCallback(() => {
    setPassages(prev => {
      const idx = prev.findIndex(p => p.isOpen);
      if (idx === -1) return prev;
      return prev.map((p, i) => (i === idx ? { ...p, isOpen: false } : p));
    });
  }, []);

  const value = useMemo<LivePassageContextValue>(
    () => ({ passages, register, unregister, setOpen, openNext, closeCurrent }),
    [passages, register, unregister, setOpen, openNext, closeCurrent],
  );

  return (
    <LivePassageContext.Provider value={value}>{children}</LivePassageContext.Provider>
  );
}

export function useLivePassageContext(): LivePassageContextValue | null {
  return useContext(LivePassageContext);
}

// Consumer hook for attachment-passage elements. Registers the element
// id on mount, removes it on unmount, and returns the entry's current
// state plus a setter that drives the modal.
export function useRegisterLivePassage(
  elementId: string,
  enabled: boolean,
): { isOpen: boolean; setOpen: (open: boolean) => void } | null {
  const ctx = useLivePassageContext();
  const { register, unregister } = ctx ?? {};

  useEffect(() => {
    if (!enabled || !register || !unregister) return;
    register(elementId);
    return () => unregister(elementId);
  }, [enabled, elementId, register, unregister]);

  if (!ctx || !enabled) return null;
  const entry = ctx.passages.find(p => p.id === elementId);
  return {
    isOpen: entry?.isOpen ?? false,
    setOpen: (open: boolean) => ctx.setOpen(elementId, open),
  };
}

// Aggregate selectors for the clicker bridge.
export function useLivePassageAggregate(): {
  hasAttachmentElement: boolean;
  isPassageOpen: boolean;
  hasPassageBeenOpened: boolean;
  openNext: () => void;
  closeCurrent: () => void;
} {
  const ctx = useLivePassageContext();
  if (!ctx) {
    return {
      hasAttachmentElement: false,
      isPassageOpen: false,
      hasPassageBeenOpened: false,
      openNext: () => undefined,
      closeCurrent: () => undefined,
    };
  }
  return {
    hasAttachmentElement: ctx.passages.length > 0,
    isPassageOpen: ctx.passages.some(p => p.isOpen),
    hasPassageBeenOpened:
      ctx.passages.length > 0 && ctx.passages.every(p => p.hasBeenOpened),
    openNext: ctx.openNext,
    closeCurrent: ctx.closeCurrent,
  };
}
